<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content class="tw-flex tw-justify-between tw-items-center">
        <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">Prescriptions</h1>
        <base-button v-if="showNewRx === true" :disabled="!organization.isActive" @click="$refs.createPrescriptionSlideOver.show()">
          New Program
          <template v-slot:icon>
            <plus-icon />
          </template>
        </base-button>
      </base-content>
    </div>
    <base-content>
      <base-text-field v-model="query" ref="searchInput" class="tw-w-full" placeholder="Search for a Prescription" size="sm" tabindex="1">
        <template #prefix>
          <search-icon class="tw-h-5 tw-w-5" />
        </template>
      </base-text-field>
      <div class="tw-flex tw-justify-end tw-w-full">
        <base-button type="flat" class="tw-mt-3" size="sm" @click="openFilters()">
          <template #icon>
            <filter-icon />
          </template>
          Filters<base-badge class="tw-ml-2" color="primary" size="sm">{{ countFilters }}</base-badge>
        </base-button>
        <export-button
          class="tw-mt-3 tw-mb-2 tw-pr-0"
          size="sm"
          type="primary"
          label="Export"
          :filename="`PredictileBusinessPrescriptions.csv`"
          :requestPayload="requestPayload"
          :url="`/businesses/${businessId}/prescriptions/export?query=${encodeURIComponent(this.query || '')}`"
          @onError="didErrorExport"
        >
        </export-button>
      </div>
      <base-table :loading="isLoading" :fields="fields" :items="prescriptions" :sort-key.sync="sortKey" :sort-desc.sync="sortDesc" class="tw-mt-2">
        <template #avatar="{ item: member }">
          <div class="tw-h-12 tw-w-12">
            <Avatar :userId="member.userId" avatarType="user" />
          </div>
        </template>
        <template #member="{ item: member }">
          <div class="tw-flex">
            <base-button type="text" size="lg" @click="view(member.userId)">
              <span class="tw-whitespace-nowrap">{{ member.fullName }}</span>
            </base-button>
          </div>
          <div class="tw-text-sm tw-text-gray-500 tw-font-normal tw-whitespace-nowrap">{{ member.businessProgramName }}</div>
        </template>

        <template #status="{ item: member }">
          <base-badge class="tw-my-auto" size="sm" :color="badgeColor(member.status)"> {{ getMemberStatus(member.status) }}</base-badge>
          <div class="tw-flex"></div>
        </template>
        <template #contact="{ item: member }">
          <base-phone-number-field class="tw-text-sm tw-font-normal" :value="member.phone" />
          <div class="tw-text-xs tw-text-gray-500 tw-font-normal tw-whitespace-nowrap">{{ member.email }}</div>
        </template>
        <template #dob="{ item: member }">
          <div class="tw-text-sm tw-text-gray-700 tw-font-normal tw-whitespace-nowrap">{{ formatDate(member.dob) }}</div>
        </template>
        <template #submittedDate="{ item: member }">
          <div class="tw-text-sm tw-text-gray-700 tw-font-normal tw-whitespace-nowrap">{{ formatDate(member.submissionDate) }}</div>
        </template>

        <template #location="{ item: member }">
          <div class="tw-flex">
            <div>
              <div class="tw-text-sm tw-text-gray-700 tw-font-normal tw-whitespace-nowrap">
                {{ member.locationName }}
              </div>
            </div>
          </div>
        </template>
        <template #actions="{ item: member }">
          <div class="tw-flex tw-items-center tw-space-x-2">
            <base-button type="flat" class="tw-whitespace-nowrap" @click="openEdit(member)">
              <template v-slot:icon>
                <edit-icon /> </template
              >Edit Status
            </base-button>
          </div>
        </template>
        <template #empty-state>
          <div class="tw-flex tw-justify-center tw-py-5">
            No Results
          </div>
        </template>
        <template #loading-row>
          <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-12 tw-w-12 tw-rounded-full" /></td>
          <td class="tw-py-3 tw-px-4">
            <div class="tw-flex">
              <base-skeleton-loader class="tw-h-3 tw-w-24" />
              <base-skeleton-loader class="tw-h-3 tw-w-12 tw-rounded-full tw-ml-3" />
            </div>
            <base-skeleton-loader class="tw-h-3 tw-w-32 tw-mt-2" />
          </td>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-4 tw-w-24 tw-mb-2" />
            <base-skeleton-loader class="tw-h-3 tw-w-24" />
          </td>
          <td class="tw-py-3 tw-px-4" v-if="organization.collectDobSetting">
            <base-skeleton-loader class="tw-h-4 tw-w-20" />
          </td>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-4 tw-w-24" />
          </td>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-3 tw-w-56" />
          </td>
          <td v-if="isLocationFiltered === false" class="tw-py-3 tw-px-4">
            <div class="tw-flex tw-space-x-6 tw-justify-center">
              <base-skeleton-loader class="tw-h-6 tw-w-20" />
            </div>
          </td>
        </template>
      </base-table>
      <base-pagination v-model="page" class="tw-mt-2" />
    </base-content>
    <prescription-filter ref="filterSlideOver" :business-id="businessId" @filter-updated="applyFilter" />
    <create-prescription-slide-over ref="createPrescriptionSlideOver" :business-id="businessId" @created="loadPage" />
    <business-member-prescription-change-status ref="editPrescriptionStatusModal" :businessId="businessId" @updated="loadPage" />
  </div>
</template>

<script>
import CreatePrescriptionSlideOver from "@/views/business/prescriptions/components/CreatePrescriptionSlideOver";
import PrescriptionFilter from "@/views/business/prescriptions/components/PrescriptionFilter";
import BusinessMemberPrescriptionChangeStatus from "@/views/business/prescriptions/components/BusinessMemberPrescriptionChangeStatus";
import { debounce } from "debounce";
import { mapGetters } from "vuex";
import moment from "moment";
import { SearchIcon, PlusIcon, FilterIcon, EditIcon } from "@/assets/svgs";
import Avatar from "@/components/Avatar.vue";
import { PRESCRIPTION_STATUS_OPTIONS } from "@/constants/constants";
import { isEqual } from "lodash";
import ExportButton from "@/components/ExportButton.vue";

const TAKE_KEY = "predictile_business_prescriptions_take";

export default {
  name: "BusinessPrescriptionsView",
  components: {
    CreatePrescriptionSlideOver,
    BusinessMemberPrescriptionChangeStatus,
    SearchIcon,
    PlusIcon,
    EditIcon,
    Avatar,
    PrescriptionFilter,
    FilterIcon,
    ExportButton,
  },
  props: {
    businessId: String,
  },
  data: function() {
    return {
      prescriptions: [],
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
      query: null,
      isLoading: false,
      filters: {
        locationsFilter: [],
        statusFilter: [],
        startDate: null,
        endDate: null,
      },
      sortKey: "member",
      sortDesc: false,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      currentTimezone: "", // Add a data property to store the timezone
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("location", ["locations"]),
    ...mapGetters("role", ["isPredictile", "isBusinessManagement", "isBusinessAdministrator", "isLocationFiltered"]),
    showNewRx() {
      if (this.isBusinessManagement(this.businessId) === true || this.isBusinessAdministrator(this.businessId) === true || this.isPredictile() === true) return true;
      else return false;
    },
    fields() {
      let columns = [];

      columns.push({ title: "", key: "avatar", thClass: "tw-w-0 tw-text-center" });
      columns.push({ title: this.organization.customerLabelSingular, key: "member", isSortable: true });
      columns.push({ title: "Status", key: "status", isSortable: true });
      columns.push({ title: "Contact", key: "contact" });
      if (this.organization.collectDobSetting) columns.push({ title: "DOB", key: "dob" });
      columns.push({ title: "Submitted", key: "submittedDate", isSortable: true });
      columns.push({ title: "Location", key: "location", isSortable: true });
      if (this.isLocationFiltered === false) columns.push({ title: "Actions", key: "actions", thClass: "tw-w-0 tw-text-center" });

      return columns;
    },
    requestPayload() {
      let locationFilterStrings = this.filters.locationsFilter
        ? this.filters.locationsFilter.map(function(item) {
            return item;
          })
        : [];
      let statusFilterFilterStrings = this.filters.statusFilter
        ? this.filters.statusFilter.map(function(item) {
            return item;
          })
        : [];

      return {
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
        StatusFilter: statusFilterFilterStrings,
        LocationsFilter: locationFilterStrings,
        sortColumn: this.sortKey,
        sortOrder: this.sortDesc === true ? "desc" : "asc",
        timeZone: this.currentTimezone,
      };
    },
    countFilters() {
      var count = 0;

      if (this.filters.locationsFilter) {
        count += this.filters.locationsFilter.length;
      }

      if (this.filters.statusFilter) {
        count += this.filters.statusFilter.length;
      }
      if (this.filters.startDate !== null || this.filters.endDate !== null) {
        count++;
      }
      return count;
    },
  },
  watch: {
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
    query() {
      this.page.skip = 0;
      this.debouncedLoadPage();
    },
    sortKey() {
      this.loadPage();
    },
    sortDesc() {
      this.loadPage();
    },
  },
  created() {
    this.detectTimezone(); // Call the timezone detection function
    this.loadPage();
    this.debouncedLoadPage = debounce(this.loadPage, 500);
  },
  mounted() {
    this.$refs.searchInput.focus();
  },
  methods: {
    getMemberStatus(status) {
      return PRESCRIPTION_STATUS_OPTIONS.find((s) => s.value === status).label;
    },
    badgeColor(status) {
      let color;
      switch (status) {
        case "accepted":
          color = "green";
          break;
        case "declined":
          color = "gray";
          break;
        case "new":
        case "contact_attempted":
        case "contact_attempted_2":
        case "contact_attempted_3":
        case "changed":
        case "on_hold":
        case "delayed_entry":
        case "clarify":
        case "refill":
        case "refill_with_change":
        case "no_contact":
          color = "yellow";
          break;
        default:
          color = "gray";
      }
      return color;
    },
    openFilters() {
      this.$refs.filterSlideOver.show();
    },
    openEdit(prescription) {
      this.$refs.editPrescriptionStatusModal.show(prescription);
    },
    applyFilter(filters) {
      if (!isEqual(this.filters, filters)) {
        this.filters = filters;
        this.loadPage();
      } else {
        this.filters = filters;
      }
    },
    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },
    view(userId) {
      window.open(`/businesses/${this.businessId}/members/${userId}/prescriptions`, "_blank");
    },
    loadPage() {
      this.isLoading = true;

      let filters = { locationsFilter: [], statusFilter: [], startDate: null, endDate: null };
      if (this.filters !== undefined) filters = this.filters;

      let url = `/businesses/${this.businessId}/prescriptions/search?skip=${this.page.skip}&take=${this.page.take}`;
      if (this.query) url += `&query=${encodeURIComponent(this.query)}`;

      this.$axios
        .post(url, {
          locationsFilter: filters.locationsFilter,
          statusFilter: filters.statusFilter,
          startDate: filters.startDate,
          endDate: filters.endDate,
          sortColumn: this.sortKey,
          sortOrder: this.sortDesc === true ? "desc" : "asc",
        })
        .then((res) => {
          this.prescriptions = res.data.items;
          this.page.take = res.data.take;
          this.page.skip = res.data.skip;
          this.page.total = res.data.total;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to load prescriptions",
            message: "We had a problem retrieving the list of prescriptions. Please reload the page to try again.",
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    formatDate(date) {
      return new moment.utc(date).local().format("M/D/YYYY");
    },
    didErrorExport(err) {
      this.EventBus.$emit("GLOBAL_ERROR", {
        title: "Failed to download CSV",
        message: "We had a problem downloading the list of prescriptions. You may want to reload the page to try again.",
        error: err,
      });
    },
    detectTimezone() {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      this.currentTimezone = timeZone; // Store the detected timezone
    },
  },
};
</script>
